<template>
  <div class="container">
    <h1 id="page-title">Ticket Types</h1>
    <p class="page-description">Add and manage type of tickets.</p>
    <main-button
      style="max-width: 305px; margin-bottom: 1rem"
      @click="openCreateModal = !openCreateModal"
      ><span class="add-symbol">+</span>Create new ticket type</main-button
    >
    <div v-if="!$apollo.loading" class="attendee_table">
      <div
        v-if="ticketTypes.length === 0"
        class="no-attendees-message-container border__left border__right"
      >
        <span class="no-attendees-message">No matching records found.</span>
      </div>
      <table-list
        :hide-checkbox="true"
        :selected.sync="selected"
        :organization-slug="organizationSlug"
        :attendees="ticketTypesTableData"
        body-style="grid-auto-columns: 50px minmax(150px, 250px) !important;"
        :visible-fields="visibleFields"
        :attendee-fields="{}"
        :pagination="pagination"
        @click="handleOpenUpdateModal"
        @sort="handleSort($event)"
      />
      <table-pagination
        :pagination="pagination"
        :total-attendees="totalTicketTypeCount"
        @change="handlePagination($event)"
        @changePerPage="handlePagination($event)"
      />
    </div>
    <ticket-type-create-modal
      v-if="openCreateModal"
      :organization-venues="organizationVenues"
      @close="openCreateModal = !openCreateModal"
      @closeModal="openCreateModal = !openCreateModal"
      @createTicket="handleCreateTicketType"
    />
    <ticket-type-update-modal
      v-if="openUpdateModal"
      :organization-venues="organizationVenues"
      :initial-values="selectedTicket"
      :create-action="false"
      @closeModal="openUpdateModal = !openUpdateModal"
      @close="openUpdateModal = !openUpdateModal"
      @deleteTicketType="handleDeleteTicketType"
      @updateTicketInfo="handleUpdateTicketType"
    />
    <simple-snackbar
      :open.sync="snackbar.open"
      :message-prop="snackbar.message"
      :color="snackbar.color"
    />
  </div>
</template>

<script>
import tableList from '../../accreditations/attendee-table/list.vue'
import TicketTypeCreateModal from '@/components/organizations/ticket-types/TicketTypeCreateModal.vue'
import TicketTypeUpdateModal from '@/components/organizations/ticket-types/TicketTypeUpdateModal.vue'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'
import tablePagination from '../../accreditations/attendee-table/pagination.vue'
import {
  GET_ORGANIZATION_ORDER_ITEM_TEMPLATES_AND_VENUES,
  CREATE_ORGANIZATION_ORDER_ITEM_TEMPLATES,
  DELETE_ORGANIZATION_ORDER_ITEM_TEMPLATES,
  UPDATE_ORGANIZATION_ORDER_ITEM_TEMPLATES,
} from '@/graphql/orderItemTemplate.graphql'
export default {
  name: 'ticket-type',
  components: {
    tableList,
    TicketTypeCreateModal,
    SimpleSnackbar,
    TicketTypeUpdateModal,
    tablePagination,
  },
  props: {
    organizationSlug: {
      type: String,
    },
  },
  data() {
    const { pagination } = this.getInitialFromQueryParams()

    return {
      ticketTypes: [],
      ticketTypesTableData: [],
      pagination,
      selected: [],
      selectedTicket: {},
      snackbar: {
        open: false,
        message: '',
        color: 'primary',
      },
      openCreateModal: false,
      openUpdateModal: false,
      organizerId: null,
      organizationVenues: [],
      visibleFields: ['Ticket type', 'Odeum', 'Price'],
      totalTicketTypeCount: 0,
    }
  },
  async mounted() {
    await this.fetchTicketTypes()
  },
  methods: {
    handleOpenUpdateModal(id) {
      this.selectedTicket = this.ticketTypes.find(ticket => ticket.id === id)
      this.openUpdateModal = !this.openUpdateModal
    },
    async handleCreateTicketType(ticketInfo) {
      this.openCreateModal = !this.openCreateModal
      const orderItemTemplateInput = this.setTicketVenueSections(ticketInfo)
      const { errors } = await this.$apollo.mutate({
        mutation: CREATE_ORGANIZATION_ORDER_ITEM_TEMPLATES,
        variables: {
          organizationSlug: this.organizationSlug,
          orderItemTemplateInput,
        },
      })
      await this.showUserMessage(errors, 'created')
    },
    async handleUpdateTicketType(ticketInfo) {
      this.openUpdateModal = !this.openUpdateModal
      const { venue, currency, ...info } = ticketInfo
      const { orderItems, ...orderItemTemplateInput } = this.setTicketVenueSections(info)

      const { errors } = await this.$apollo.mutate({
        mutation: UPDATE_ORGANIZATION_ORDER_ITEM_TEMPLATES,
        variables: {
          organizationSlug: this.organizationSlug,
          orderItemTemplateInput,
        },
      })
      await this.showUserMessage(errors, 'updated')
    },
    async handleDeleteTicketType(orderItemTemplateId) {
      this.openUpdateModal = !this.openUpdateModal
      const { errors } = await this.$apollo.mutate({
        mutation: DELETE_ORGANIZATION_ORDER_ITEM_TEMPLATES,
        variables: {
          organizationSlug: this.organizationSlug,
          orderItemTemplateId,
        },
      })
      await this.showUserMessage(errors, 'deleted')
    },
    async showUserMessage(errors, msg) {
      if (errors) {
        this.snackbar = {
          open: true,
          message: 'Oops, something went wrong!',
          color: 'error',
        }
      } else {
        this.snackbar = {
          open: true,
          message: `Successfully ${msg} ticket type!`,
        }

        this.selected = []
        await this.fetchTicketTypes()
      }
    },
    setTicketVenueSections(ticketInfo) {
      if (
        ticketInfo.venueId &&
        (!ticketInfo.venueSection.length || ticketInfo.venueSection.includes('All'))
      ) {
        const selectedVenue = this.organizationVenues.find(venue => venue.id == ticketInfo.venueId)
        if (selectedVenue.predefinedSections && selectedVenue.seats) {
          ticketInfo.venueSection = Object.keys(selectedVenue.predefinedSections)
        }
      }

      return ticketInfo
    },
    async handlePagination({ page, per_page }) {
      const { page: currentPage, per_page: currentPerPage } = this.pagination
      if (currentPage > page && currentPage === 1) return
      if (page > currentPage && !(this.totalTicketTypeCount - currentPerPage * currentPage > 0))
        return

      this.pagination = {
        ...this.pagination,
        per_page: per_page === 'All' ? -1 : per_page,
        page: page,
      }

      await this.addPaginationToRoute(this.pagination)
    },
    async handleSort(sortBy) {
      const { per_page, page, sortBy: thisSortBy, descending } = this.pagination
      const pagination = {
        page,
        per_page,
        sortBy,
        descending: sortBy === thisSortBy ? !descending : false,
      }
      await this.addPaginationToRoute(pagination)
      this.pagination = pagination
    },
    getInitialFromQueryParams() {
      const { per_page, page, sortBy, descending } = this.$route.query

      return {
        pagination: {
          page: parseInt(page, 10) || 1,
          per_page: parseInt(per_page, 10) || 10,
          sortBy: sortBy,
          descending: descending ? !!descending : false,
        },
      }
    },
    async addPaginationToRoute({ page, per_page, sortBy, descending }) {
      await this.$router.replace({
        query: {
          ...this.$route.query,
          per_page,
          page,
          sortBy,
          descending: descending ? 1 : 0,
        },
      })

      await this.fetchTicketTypes()
    },
    async fetchTicketTypes() {
      const {
        data: { getOrganizationOrderItemTemplates, getOrganizationVenues },
        errors,
      } = await this.$apollo.query({
        query: GET_ORGANIZATION_ORDER_ITEM_TEMPLATES_AND_VENUES,
        variables: {
          organizationSlug: this.organizationSlug,
          pagination: this.pagination,
        },
      })

      if (errors) {
        await this.showUserMessage(errors)
      } else {
        this.organizationVenues = getOrganizationVenues.organizationVenues.map(
          organizationVenue => {
            return {
              ...organizationVenue.venue,
              id: parseInt(organizationVenue.venue.id),
            }
          },
        )
        this.totalTicketTypeCount = getOrganizationOrderItemTemplates.count
        this.ticketTypes = getOrganizationOrderItemTemplates

        this.ticketTypesTableData =
          getOrganizationOrderItemTemplates.map(ticket => {
            const venueName = ticket.venue ? ticket.venue.name : ''

            return {
              id: ticket.id,
              'ticket type': ticket.name,
              odeum: venueName,
              price: ticket.price,
              currency: ticket.currency,
            }
          }) || {}
      }
    },
  },
}
</script>

<style scoped>
.page-description {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  color: #979797;
}
.container {
  padding: 0 !important;
  margin: 0 !important;
  display: flex;
  flex-direction: column;
  max-width: 43.75rem;
}
#page-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 1.5rem;
}
</style>
